import {action, computed} from 'mobx';
import {ITreeNode} from '@techsee/techsee-ui-common/lib/simple-tree-control-view';
import {IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {EventLogService} from '../../../../../services/event-log/event-log.service';
import isEmpty from 'lodash/isEmpty';
import {getRootStore} from '../../../../../app.bootstrap';
import {uuidv4} from '@techsee/techsee-common/lib/core/uuid';
import {BaseTreeController} from '../../../BaseTreeController';

export enum PredefinedTreeEvents {
    LOAD_TEXT_MESSAGE = 'LOAD_TEXT_MESSAGE',
    SET_MSG_SECTION_OPEN = 'SET_MSG_SECTION_OPEN',
    OPEN_TEXT_DIALOG = 'OPEN_TEXT_DIALOG'
}

export class PredefinedTreeController extends BaseTreeController<PredefinedTreeEvents> {
    constructor(
        accountId: string,
        userId: string,
        owners: string,
        resourceService: IResourcesService,
        eventLog: EventLogService,
        trace: any,
        useNewInterface: boolean
    ) {
        super(accountId, userId, owners, resourceService, eventLog, trace, useNewInterface);

        this._treeNode.data.title = 'predefined';

        this._loadPredefinedResources();
    }

    @action
    selectNode = (node: ITreeNode): void => {
        if (node && node.data.type !== 'dir') {
            this.predefinedResourceSelected(node);
        }
    };

    @computed
    get empty(): boolean {
        // Check if there are any resource nodes under the predefined folder
        if (!this._treeNode.children || isEmpty(this._treeNode.children)) {
            return true;
        }

        const firstChild = this._treeNode.children[0];
        return this._treeNode.children.length === 1 && isEmpty(firstChild?.children);
    }

    @action
    private predefinedResourceSelected(node: ITreeNode): void {
        const resource = node.data.resource;

        if (!resource) {
            return;
        }

        let message = '';
        if (resource.message) {
            message = `${resource.message}\n\n${resource.url}`;
        } else {
            message = resource.url;
        }

        // Open the text dialog modal with the predefined message
        this.emit(PredefinedTreeEvents.OPEN_TEXT_DIALOG, message);
    }

    @action
    private _loadPredefinedResources = (): Promise<void> | void => {
        if (this.accountId) {
            return this.resourceService
                .findAll({
                    accountId: this.accountId,
                    owners: this.owners || undefined,
                    predefined: true
                })
                .then((resources) => {
                    this._buildTree(resources);

                    this.trace.info(
                        `Predefined resources loading success - currentUser: ${this.userId}, resources count: ${resources.length}`
                    );
                })
                .catch((err) => {
                    this.eventLog.libraryLoadingFailed({
                        currentUserId: this.userId,
                        error: err?.message || JSON.stringify(err).slice(0, 1000)
                    });
                });
        }
    };

    @action
    private _buildTree(resources: any[]): void {
        const translate = getRootStore().localizationService.translate;
        const predefinedLabel = translate('MAIN.VIEW.PREDEFINED') || 'Predefined Messages';

        const resourceNodes = resources.map((resource) => ({
            id: uuidv4(),
            parents: ['root', predefinedLabel],
            children: [],
            state: {expanded: true, selected: false, disabled: false},
            data: {
                title: resource.title,
                type: resource.type,
                url: resource.url,
                resource: resource
            }
        }));

        this._treeNode = {
            id: uuidv4(),
            parents: [],
            children: [
                {
                    id: uuidv4(),
                    parents: ['root'],
                    children: resourceNodes,
                    state: {expanded: true, selected: false, disabled: false},
                    data: {title: predefinedLabel, type: 'dir'}
                }
            ],
            state: {expanded: true, selected: false, disabled: false},
            data: {title: 'root', type: 'dir'}
        };
    }
}
